import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { getJSON } from '@/lib/api'
import type { Association } from '@/pages/StaffSignInPage'

interface PublicCampaign {
  id: number
  name: string
  raised: string
  goal: string | null
  goal_cents: number | null
  progress_percent: number | null
  ended: boolean
  closed_on: string | null
  association_name: string
  tax_wording: string
  give_url: string
}

export function PublicCampaignPage({
  association,
  campaignId,
}: {
  association: Association
  campaignId: number
}) {
  const query = useQuery({
    queryKey: ['public-campaign', campaignId],
    queryFn: () => getJSON<{ campaign: PublicCampaign }>(`/api/v1/public/campaigns/${campaignId}`),
    retry: false,
  })

  if (query.isLoading)
    return (
      <main className="min-h-screen bg-background p-6">
        <p className="text-body-sm text-muted-foreground" role="status">
          Loading Campaign…
        </p>
      </main>
    )
  if (query.isError || !query.data)
    return (
      <main className="min-h-screen bg-background p-6">
        <Alert>Campaign not found.</Alert>
      </main>
    )

  const campaign = query.data.campaign
  const progress = Math.min(campaign.progress_percent ?? 0, 100)
  return (
    <main className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border">
        <div className="mx-auto flex h-14 max-w-6xl items-center px-5 text-body-sm font-medium">
          {association.name}
        </div>
      </header>
      <div className="mx-auto grid max-w-6xl gap-10 px-5 py-8 lg:grid-cols-[minmax(0,1fr)_320px]">
        <section className="space-y-5">
          <div
            className="aspect-[3/1] w-full rounded bg-gradient-to-br from-primary/20 via-accent to-muted"
            role="img"
            aria-label={`${campaign.name} campaign artwork`}
          />
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded bg-primary text-caption text-primary-foreground">
              {association.name.slice(0, 1)}
            </span>
            <span className="text-caption text-muted-foreground">{campaign.association_name}</span>
          </div>
          <h1 className="text-heading font-semibold tracking-tight">{campaign.name}</h1>
          <p className="max-w-2xl text-body-sm leading-relaxed text-muted-foreground">
            Help support the work of {campaign.association_name}.
          </p>
        </section>
        <aside className="space-y-4 self-start">
          <section className="space-y-4 rounded-md border border-border bg-card p-4">
            {campaign.ended ? (
              <p className="text-caption text-muted-foreground">
                {campaign.raised} raised · Campaign ended {campaign.closed_on}
              </p>
            ) : (
              <>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-heading-sm font-semibold tabular-nums">{campaign.raised}</p>
                    <p className="text-caption text-muted-foreground">Raised</p>
                  </div>
                  {campaign.goal && (
                    <div className="text-right">
                      <p className="text-body font-medium tabular-nums">{campaign.goal}</p>
                      <p className="text-caption text-muted-foreground">Goal</p>
                    </div>
                  )}
                </div>
                {campaign.goal && (
                  <Progress
                    value={progress}
                    aria-label={`${campaign.progress_percent}% progress`}
                  />
                )}
              </>
            )}
            {!campaign.ended && (
              <Button
                type="button"
                variant="outline"
                className="h-8 w-full px-3"
                onClick={() => navigator.clipboard?.writeText(window.location.href)}
              >
                Share
              </Button>
            )}
            <Button
              asChild
              variant={campaign.ended ? 'outline' : 'default'}
              className="h-8 w-full px-3"
            >
              <a href={campaign.ended ? '/give' : campaign.give_url}>
                {campaign.ended ? `Give to ${association.name}` : 'Donate'}
              </a>
            </Button>
          </section>
          <section className="rounded-md border border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded bg-primary text-body-sm text-primary-foreground">
                {association.name.slice(0, 1)}
              </span>
              <div>
                <p className="text-body-sm font-medium text-foreground">
                  {campaign.association_name}
                </p>
                <p className="text-caption text-muted-foreground">Giving with {association.name}</p>
              </div>
            </div>
            <p className="mt-4 text-caption text-muted-foreground">{campaign.tax_wording}</p>
          </section>
        </aside>
      </div>
    </main>
  )
}
